import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { OrganizationProvider } from "./context/OrganizationContext";
import { FeatureFlagsProvider, FeatureRoute } from "./context/FeatureFlagsContext";
import { ContextGuard } from "./components/ContextGuard";
import { SubscriptionGate } from "./components/SubscriptionGate";
import { Layout } from "./components/Layout";
import { DashboardPage } from "./pages/DashboardPage";
import { UploadPage } from "./pages/UploadPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { ActivityPage } from "./pages/ActivityPage";
import { TeamPage } from "./pages/TeamPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { BillingPage } from "./pages/BillingPage";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { TermsPage } from "./pages/legal/TermsPage";
import { PrivacyPage } from "./pages/legal/PrivacyPage";
import { useEffect } from "react";
import { usePageViewAudit } from "../../shared/audit";
import { logPageView, logUserEvent } from "./modules/upload-inbox/api";

const LOGIN_LOGGED_KEY = "haven.audit.login_logged";

/**
 * Fires a page-view audit on every route change and a one-time
 * `user.login` when the session starts. Rendered inside ContextGuard,
 * so it only runs once auth + org context are ready — the audit
 * endpoint requires both.
 *
 * The login guard is per-browser-session (sessionStorage); the server
 * also dedupes within 30s, but the flag avoids a redundant request on
 * F5 refreshes of an already-authenticated tab. Logout clears the flag
 * so a subsequent login re-fires.
 */
function AuditTracker() {
  const location = useLocation();
  usePageViewAudit(`${location.pathname}${location.search}`, logPageView);
  useEffect(() => {
    if (sessionStorage.getItem(LOGIN_LOGGED_KEY)) return;
    sessionStorage.setItem(LOGIN_LOGGED_KEY, "1");
    logUserEvent("login");
  }, []);
  return null;
}

/**
 * Shell App - Main application component
 * Requirements: 6.1, 9.4, 9.5, 15.1
 */
export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrganizationProvider>
          <Routes>
            {/* Public — must render OUTSIDE ContextGuard so an unauth
                invitee can preview the invitation before signing in.
                Once they click "Continue to sign in," Auth0's returnTo
                brings them back here with a live session, and the page
                re-renders in its "accept/decline" state. */}
            <Route path="/invitations/accept" element={<AcceptInvitationPage />} />
            {/* Public legal pages — linked from the signed-out screens and
                required by Stripe, so they must work without a session. */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Everything else is gated by ContextGuard (auth + org). */}
            <Route
              path="/*"
              element={
                <ContextGuard>
                  <FeatureFlagsProvider>
                    <Layout>
                      <AuditTracker />
                      {/* New orgs choose a plan before anything else. */}
                      <SubscriptionGate>
                      <Routes>
                        {/* MVP routes are always on; the rest are tester
                            feature flags and redirect to /documents when off. */}
                        <Route path="/" element={<FeatureRoute feature="dashboard"><DashboardPage /></FeatureRoute>} />
                        <Route path="/upload" element={<UploadPage />} />
                        <Route path="/documents" element={<DocumentsPage />} />
                        <Route path="/activity" element={<FeatureRoute feature="activity_log"><ActivityPage /></FeatureRoute>} />
                        <Route path="/team" element={<FeatureRoute feature="team"><TeamPage /></FeatureRoute>} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/settings" element={<FeatureRoute feature="settings_page"><SettingsPage /></FeatureRoute>} />
                        <Route path="/profile" element={<ProfilePage />} />
                      </Routes>
                      </SubscriptionGate>
                    </Layout>
                  </FeatureFlagsProvider>
                </ContextGuard>
              }
            />
          </Routes>
        </OrganizationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
